import { useMemo } from "react";
import { useCheckConvertEligibility } from "../api/product";

export interface ConvertToAverageEligibility {
  isEligible: boolean;
  hasOneSalesUnit: boolean;
  hasNoMapping: boolean;
  hasClimateImpact: boolean;
  isAverageProduct: boolean;
  salesUnitsCount: number;
  reason: string;
  isLoading: boolean;
  error: any;
}

/**
 * Custom hook to check if a product is eligible for conversion to Average Impact product
 * Wraps the API eligibility check and provides a clean interface
 *
 * @param productId - The ID of the product to check
 * @returns Eligibility status with detailed reasons and loading/error states
 *
 * @example
 * const { isEligible, reason, isLoading } = useConvertToAverageEligibility(productId);
 *
 * if (isLoading) return <Loader />;
 * if (!isEligible) return <Tooltip content={reason}><Button disabled /></Tooltip>;
 */
export const useConvertToAverageEligibility = (
  productId: string | undefined
): ConvertToAverageEligibility => {
  const { data, isLoading, error } = useCheckConvertEligibility(productId);

  return useMemo(() => {
    if (!data) {
      return {
        isEligible: false,
        hasOneSalesUnit: false,
        hasNoMapping: false,
        hasClimateImpact: false,
        isAverageProduct: false,
        salesUnitsCount: 0,
        reason: isLoading ? "Checking eligibility..." : "Unable to check eligibility",
        isLoading,
        error
      };
    }

    return {
      ...data,
      isLoading,
      error
    };
  }, [data, isLoading, error]);
};
